import {
    CallbackType,
    CallGroupType,
    ClientType,
    DirectionType,
    DomainType,
    ExtensionProfileRuleAction,
    FeatureRestriction,
    HuntingAlgorithmType,
    NonPaymentBlockingType,
    PhoneButtonType,
    PrivateRoutingType,
    SecretaryMode,
    UserProfileRuleMatchType,
    Weekday,
} from "@/api";
import {defineMessage, defineMessages} from "react-intl";

export const msg_close = defineMessage({id: "close", defaultMessage: "Close"});
export const msg_delete = defineMessage({id: "delete", defaultMessage: "Delete"});
export const msg_remove = defineMessage({id: "remove", defaultMessage: "Remove"});
export const msg_update = defineMessage({id: "update", defaultMessage: "Update"});
export const msg_clear = defineMessage({id: "clear", defaultMessage: "Clear"});
export const msg_add = defineMessage({id: "add", defaultMessage: "Add"});
export const msg_import = defineMessage({id: "import", defaultMessage: "Import"});

export const CONFIRM_MESSAGES = defineMessages({
    delete_device_variant: {
        id: "confirms.delete_device_variant",
        defaultMessage: 'Do you really want to delete "{name}"?',
    },
    delete_domain: {
        id: "confirms.delete_domain",
        defaultMessage: 'Do you really want to delete "{name}"?',
    },
    delete_extension_user: {
        id: "confirms.delete_extension_user",
        defaultMessage: 'Do you really want to remove "{user}" from this list?',
    },
    delete_ivr: {
        id: "confirms.delete_ivr",
        defaultMessage: 'Do you really want to delete "{name}"?',
    },
    delete_location: {
        id: "confirms.delete_location",
        defaultMessage: "Do you really want to delete this location?",
    },
    delete_pickup_group: {
        id: "confirms.delete_pickup_group",
        defaultMessage: 'Do you really want to delete "{name}"?',
    },
    delete_speed_dial: {
        id: "confirms.delete_speed_dial",
        defaultMessage: 'Do you really want to delete "{name}"?',
    },
    general_delete: {
        id: "confirms.general_delete",
        defaultMessage: 'Do you really want to delete "{name}"?',
    },
    make_owner: {
        id: "confirms.make_owner",
        defaultMessage: 'Are you sure you want to make "{login}" the owner of the user?',
    },
    resendInvitationEmail: {
        id: "confirms.resendInvitationEmail",
        defaultMessage: 'Are you sure you want to resend the invitation email for "{user}"?',
    },
});

// Static, exhaustive map for Weekday enum to avoid dynamic IDs
export const WEEKDAY_MESSAGES = defineMessages<Weekday>({
    MONDAY: {id: "Weekday.MONDAY", defaultMessage: "Monday"},
    TUESDAY: {id: "Weekday.TUESDAY", defaultMessage: "Tuesday"},
    WEDNESDAY: {id: "Weekday.WEDNESDAY", defaultMessage: "Wednesday"},
    THURSDAY: {id: "Weekday.THURSDAY", defaultMessage: "Thursday"},
    FRIDAY: {id: "Weekday.FRIDAY", defaultMessage: "Friday"},
    SATURDAY: {id: "Weekday.SATURDAY", defaultMessage: "Saturday"},
    SUNDAY: {id: "Weekday.SUNDAY", defaultMessage: "Sunday"},
});

export const HUNTING_ALGORITHM_TYPE_MESSAGES = defineMessages<HuntingAlgorithmType>({
    UCD: {id: "HuntingAlgorithmType.UCD", defaultMessage: "Least Recently Serving"},
    CIRCLE: {id: "HuntingAlgorithmType.CIRCLE", defaultMessage: "Rotary"},
    LINEAR: {id: "HuntingAlgorithmType.LINEAR", defaultMessage: "Fixed Order"},
    NON_FORCED: {id: "HuntingAlgorithmType.NON_FORCED", defaultMessage: "Non Forced"},
});

export const DOMAIN_TYPE_MESSAGES = defineMessages<DomainType>({
    BUSINESS: {id: "domain_type.BUSINESS", defaultMessage: "Business"},
    RESIDENTIAL: {id: "domain_type.RESIDENTIAL", defaultMessage: "Residential"},
});

export const PRIVATE_ROUTING_TYPE_MESSAGES = defineMessages<PrivateRoutingType>({
    GATEWAY_ONLY: {id: "PrivateRoutingType.GATEWAY_ONLY", defaultMessage: "Gateway only"},
    GATEWAY_WITH_PSTN_FALLBACK: {
        id: "PrivateRoutingType.GATEWAY_WITH_PSTN_FALLBACK",
        defaultMessage: "Gateway with PSTN fallback",
    },
    PSTN_ONLY: {id: "PrivateRoutingType.PSTN_ONLY", defaultMessage: "PSTN only"},
});

export const CLIENT_TYPE_MESSAGES = defineMessages<ClientType>({
    ALL: {id: "client_type.ALL", defaultMessage: "All"},
    DESKTOP: {id: "client_type.DESKTOP", defaultMessage: "Desktop"},
    MOBILE: {id: "client_type.MOBILE", defaultMessage: "Mobile"},
});

export const USER_PROFILE_RULE_MATCH_TYPE_MESSAGES = defineMessages<UserProfileRuleMatchType>({
    contains: {id: "UserProfileRuleMatchType.contains", defaultMessage: "Contains"},
    ends_with: {id: "UserProfileRuleMatchType.ends_with", defaultMessage: "Ends with"},
    identical: {id: "UserProfileRuleMatchType.identical", defaultMessage: "Identical"},
    starts_with: {id: "UserProfileRuleMatchType.starts_with", defaultMessage: "Starts with"},
});

export const EXTENSION_PROFILE_RULE_ACTION_MESSAGES = defineMessages<ExtensionProfileRuleAction>({
    ANSWER: {id: "ExtensionProfileRuleAction.ANSWER", defaultMessage: "Answer"},
    ANSWER_IMMEDIATELY: {id: "ExtensionProfileRuleAction.ANSWER_IMMEDIATELY", defaultMessage: "Answer immediately"},
    DISTINCTIVE_RINGING: {id: "ExtensionProfileRuleAction.DISTINCTIVE_RINGING", defaultMessage: "Distinctive ringing"},
    FORWARD: {id: "ExtensionProfileRuleAction.FORWARD", defaultMessage: "Forward"},
    REJECT: {id: "ExtensionProfileRuleAction.REJECT", defaultMessage: "Reject"},
});

export const SECRETARY_MODE_MESSAGES = defineMessages<SecretaryMode>({
    BOSS_FLASHING: {id: "SecretaryMode.BOSS_FLASHING", defaultMessage: "Boss flashing"},
    BOSS_ONLY: {id: "SecretaryMode.BOSS_ONLY", defaultMessage: "Boss only"},
    BOTH_RINGING: {id: "SecretaryMode.BOTH_RINGING", defaultMessage: "Both ringing"},
    SECRETARY_ONLY: {id: "SecretaryMode.SECRETARY_ONLY", defaultMessage: "Secretary only"},
});

export const CALLBACK_TYPE_MESSAGES = defineMessages<CallbackType>({
    ALWAYS_CALLBACK_NUMBER: {
        id: "CallbackType.ALWAYS_CALLBACK_NUMBER",
        defaultMessage: "The specified callback number",
    },
    ACCEPT_RECEIVED_CALLED_NUMBER: {
        id: "CallbackType.ACCEPT_RECEIVED_CALLED_NUMBER",
        defaultMessage: "Accept received called number",
    },
    MATCH_ACCORDING_OR_FALL_BACK: {
        id: "CallbackType.MATCH_ACCORDING_OR_FALL_BACK",
        defaultMessage: "Match to recent callers of fall back to callback number",
    },
    PREVIOUS_OPTIONS_WITH_DISA: {
        id: "CallbackType.PREVIOUS_OPTIONS_WITH_DISA",
        defaultMessage: "Match to callers or ask for destination number",
    },
});

export const NON_PAYMENT_BLOCKING_TYPE_MESSAGES = defineMessages<NonPaymentBlockingType>({
    INCOMING_AND_OUTGOING_BLOCKED: {
        id: "NonPaymentBlockingType.INCOMING_AND_OUTGOING_BLOCKED",
        defaultMessage: "Incoming and outgoing blocked",
    },
    NOT_BLOCKED: {id: "NonPaymentBlockingType.NOT_BLOCKED", defaultMessage: "Not blocked"},
    OUTGOING_BLOCKED: {id: "NonPaymentBlockingType.OUTGOING_BLOCKED", defaultMessage: "Outgoing blocked"},
});

export const DIRECTION_TYPE_MESSAGES = defineMessages<DirectionType>({
    BOTH: {id: "DirectionType.BOTH", defaultMessage: "Both"},
    IN: {id: "DirectionType.IN", defaultMessage: "Incoming"},
    NONE: {id: "DirectionType.NONE", defaultMessage: "None"},
    OUT: {id: "DirectionType.OUT", defaultMessage: "Outgoing"},
});

export const CALL_GROUP_TYPE_MESSAGES = defineMessages<CallGroupType>({
    SERIAL: {id: "CallGroupType.SERIAL", defaultMessage: "Serial ringing"},
    PARALLEL: {id: "CallGroupType.PARALLEL", defaultMessage: "Parallel ringing"},
    CALL_QUEUE: {id: "CallGroupType.CALL_QUEUE", defaultMessage: "Call queue"},
});

export const PHONE_BUTTON_TYPE_MESSAGES = defineMessages<PhoneButtonType>({
    BLF: {id: "PhoneButtonType.BLF", defaultMessage: "BLF"},
    BLF_GROUP: {id: "PhoneButtonType.BLF_GROUP", defaultMessage: "BLF Group"},
    CALLPARK: {id: "PhoneButtonType.CALLPARK", defaultMessage: "Call park"},
    EMPTY: {id: "PhoneButtonType.EMPTY", defaultMessage: "Empty"},
    LINE: {id: "PhoneButtonType.LINE", defaultMessage: "Line"},
    SPEED_DIAL: {id: "PhoneButtonType.SPEED_DIAL", defaultMessage: "Speed dial"},
});

export const FEATURE_RESTRICTION_MESSAGES = defineMessages<FeatureRestriction>({
    CALL_REJECTIONS: {id: "FeatureRestriction.CALL_REJECTIONS", defaultMessage: "Call rejections"},
    CALL_ROUTINGS: {id: "FeatureRestriction.CALL_ROUTINGS", defaultMessage: "Call routings"},
    LOCKING: {id: "FeatureRestriction.LOCKING", defaultMessage: "Locking"},
    NUMBER_PRESENTATION: {id: "FeatureRestriction.NUMBER_PRESENTATION", defaultMessage: "Number presentation"},
    SPEED_DIALS: {id: "FeatureRestriction.SPEED_DIALS", defaultMessage: "Speed dials"},
});

/// Generate a label message for a capability set or capability
/// This relies on having the translation manually added to the translation files.
export function labelForCapabilitySet(capability: string) {
    // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
    return defineMessage({id: `capability_set_names.${capability}`, defaultMessage: capability});
}

/// Generate a label message for a device type set
/// This relies on having the translation manually added to the translation files.
export function labelForDeviceTypeSet(deviceTypeSet: string) {
    // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
    return defineMessage({id: `device_type_set_names.${deviceTypeSet}`, defaultMessage: deviceTypeSet});
}

/// Generate a label message for a user package
/// This relies on having the translation manually added to the translation files.
export function labelForUserPackage(userPackage: string) {
    // eslint-disable-next-line formatjs/enforce-default-message, formatjs/enforce-id
    return defineMessage({id: `user_package_names.${userPackage}`, defaultMessage: userPackage});
}

export const VOICEMAIL_FETCH_ERRORS = defineMessages({
    extension: {
        id: "voicemail.fetcherror.extension",
        defaultMessage: "User does not have a voice mailbox",
    },
    voicemail_api: {
        id: "voicemail.fetcherror.voicemail_api",
        defaultMessage: "Voicemail service error",
    },
    delete: {
        id: "voicemail.fetcherror.delete",
        defaultMessage: "Could not mark voicemail as deleted",
    },
    listen: {
        id: "voicemail.fetcherror.listen",
        defaultMessage: "Could not mark voicemail as listened",
    },
    decoding: {
        id: "voicemail.fetcherror.decoding",
        defaultMessage: "Could not decode voicemail message",
    },
});

export const SERVICE_NAME_MESSAGES = defineMessages({
    fax: {id: "service_names.fax", defaultMessage: "Fax"},
    gateway: {id: "service_names.gateway", defaultMessage: "Gateway"},
    group5: {id: "service_names.group5", defaultMessage: "Group (5 members)"},
    group10: {id: "service_names.group10", defaultMessage: "Group (10 members)"},
    group20: {id: "service_names.group20", defaultMessage: "Group (20 members)"},
    ccBase: {id: "service_names.ccBase", defaultMessage: "Call Center"},
    ccAattendant: {id: "service_names.ccAattendant", defaultMessage: "Additional attendant"},
    ccQueue: {id: "service_names.ccQueue", defaultMessage: "Call queue"},
});

/**
 * Common translations used in multiple places, to avoid duplicated definitions.
 * Not using defineMessages, to be able to group them better.
 * */
export const commonTranslations = {
    // eslint-disable-next-line formatjs/enforce-id
    "#": defineMessage({id: "#", defaultMessage: "#"}),
    delete: defineMessage({id: "delete", defaultMessage: "Delete"}),
    vendor: defineMessage({id: "vendor", defaultMessage: "Vendor"}),
    stationName: defineMessage({id: "station_name", defaultMessage: "Station name"}),
    macAddress: defineMessage({id: "mac_address", defaultMessage: "MAC address"}),
    provisioning: defineMessage({id: "provisioning", defaultMessage: "Provisioning"}),
    variant: defineMessage({id: "variant", defaultMessage: "Variant"}),
    default: defineMessage({id: "default", defaultMessage: "Default"}),
    useDisplayNameForStationName: {
        useE164: defineMessage({
            id: "use_e164",
            defaultMessage: "Use E.164 number",
        }),
        useDisplayName: defineMessage({
            id: "use_display_name",
            defaultMessage: "Use display name",
        }),
    },
    action: defineMessage({id: "action", defaultMessage: "Action"}),
    email: defineMessage({id: "email", defaultMessage: "Email"}),
    number: defineMessage({id: "number", defaultMessage: "Number"}),
    display: defineMessage({id: "display", defaultMessage: "Display"}),
    devices: {
        provisioned: {
            dialogTitles: {
                update: defineMessage({
                    id: "devices.provisioned.update",
                    defaultMessage: "Update provisioned device: {name}",
                }),
                view: defineMessage({
                    id: "devices.provisioned.view",
                    defaultMessage: "Provisioned device: {name}",
                }),
            },
            managePhoneButtons: defineMessage({
                id: "manage_phone_buttons",
                defaultMessage: "Manage phone buttons",
            }),
            phoneButtons: defineMessage({
                id: "phone_buttons",
                defaultMessage: "Phone buttons",
            }),
        },
    },
    filterPlaceholders: {
        nameOrPhoneNumber: defineMessage({
            id: "filter_placeholders.name_or_phone_number",
            defaultMessage: "Name or phone number",
        }),
        nameOrE164OrShortNumber: defineMessage({
            id: "filter_placeholders.name_or_e164_or_short_number",
            defaultMessage: "Name, E.164 or <identifier>@<domain>",
        }),
        nameOrDescription: defineMessage({
            id: "filter_placeholders.name_or_description",
            defaultMessage: "Name or description",
        }),
        nameOrDomainName: defineMessage({
            id: "filter_placeholders.name_or_domain_name",
            defaultMessage: "Name or domain name",
        }),
        nameOrDescriptionOrPilotNumber: defineMessage({
            id: "filter_placeholders.name_or_description_or_pilot_number",
            defaultMessage: "Name, description or pilot number",
        }),
        e164OrShortNumber: defineMessage({
            id: "filter_placeholders.e164_or_short_number",
            defaultMessage: "E.164 or <identifier>@<domain>",
        }),
        name: defineMessage({
            id: "filter_placeholders.name",
            defaultMessage: "Name",
        }),
        displayNameOrMobileOrE164OrShortNumber: defineMessage({
            id: "filter_placeholders.display_name_or_mobile_or_e164_or_short_number",
            defaultMessage: "Display name, mobile number, E.164 or <identifier>@<domain>",
        }),
    },
    canCreateEndpoints: defineMessage({
        id: "admin.canCreateEndpoint",
        defaultMessage: "Can create endpoints",
    }),
    language: defineMessage({
        id: "language",
        defaultMessage: "Language",
    }),
    systemDefault: defineMessage({id: "system_default", defaultMessage: "System default"}),
    notSet: defineMessage({id: "not_set", defaultMessage: "Not set"}),
};
